import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import SupportTicketModal from './SupportTicketModal';
import { getConsoleErrors } from '../../utils/errorBuffer';
import { canvasToCompressedDataURL } from './supportConstants';

// Routes où le bouton flottant ne doit PAS apparaître (publiques / auth)
const HIDDEN_PREFIXES = ['/f/', '/p/', '/shared/', '/login', '/logout', '/super-admin'];

function isAuthenticatedNonExternal() {
  const token = localStorage.getItem('token');
  if (!token) return false;
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user') || '{}');
  } catch (_) {
    user = {};
  }
  // Whitelist stricte : authentifié ET rôle != 'external'
  return user.role !== 'external';
}

/**
 * Bouton flottant "Signaler un problème".
 * - Visible uniquement si utilisateur authentifié && role != 'external'.
 * - Capture l'écran (html2canvas) AVANT d'ouvrir la modale (la modale n'apparaît pas dans la capture).
 */
const SupportFloatingButton = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [consoleErrors, setConsoleErrors] = useState([]);

  const path = location.pathname || '';
  const hidden = HIDDEN_PREFIXES.some((p) => path === p || path.startsWith(p));

  // En mode ÉDITION d'une fiche bien, le footer contient les boutons Précédent/
  // Suivant qui occupent toute la largeur, avec « Suivant » à droite. Pour
  // éviter le chevauchement de la bulle « ? » avec le bouton « Suivant », on
  // bascule la bulle à GAUCHE en mobile sur ces pages (position: fixed).
  // Desktop : comportement inchangé (right-6, plus large, pas de conflit).
  const isEdit = /^\/properties\/[^/]+\/edit\b/.test(path);

  const handleOpen = useCallback(async () => {
    setConsoleErrors(getConsoleErrors());
    setCapturing(true);
    setScreenshot(null);
    setOpen(true);
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        scale: 1,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setScreenshot(canvasToCompressedDataURL(canvas));
    } catch (e) {
      // Capture non bloquante — le ticket peut partir sans screenshot
      setScreenshot(null);
    } finally {
      setCapturing(false);
    }
  }, []);

  // lot DK — téléphone : l'entrée « Signaler un problème » du menu du compte ouvre la même fenêtre
  useEffect(() => {
    window.addEventListener('ouvrir-signalement', handleOpen);
    return () => window.removeEventListener('ouvrir-signalement', handleOpen);
  }, [handleOpen]);

  if (hidden || !isAuthenticatedNonExternal()) return null;

  return (
    <>
      {!open && (
        <button
          onClick={handleOpen}
          title="Signaler un problème"
          data-testid="support-floating-btn"
          className={`hidden md:flex fixed z-40 bottom-6 ${isEdit ? 'md:right-6' : 'md:right-6'} h-12 w-12 rounded-full bg-slate-900 hover:bg-slate-700 text-white shadow-lg hover:shadow-xl transition-all items-center justify-center group`}
        >
          <i className="bi bi-life-preserver text-lg"></i>
        </button>
      )}

      <SupportTicketModal
        open={open}
        onClose={() => setOpen(false)}
        screenshot={screenshot}
        capturing={capturing}
        consoleErrors={consoleErrors}
        onSubmitted={() => toast.success('Signalement transmis à notre équipe.')}
      />
    </>
  );
};

export default SupportFloatingButton;
